import React, { useEffect, useState } from 'react';
import DetailedListComponent, { DetailedListColumn } from '../../common/DetailedList/DetailedListComponent';
import SubsectionPanel from '../../common/SubsectionPanel/SubsectionPanel';
import { ProjectPhasesRestDataProvider, ProjectPhase } from '../../../data/ProjectPhasesRestDataProvider';
import './ProjectPhasesTab.scss';
import InfoPanel from '../../common/InfoPanel/InfoPanel';
import { useTranslation } from 'react-i18next';

const ProjectPhasesTab: React.FC = () => {
  const { t } = useTranslation(['common', 'screens']);
  const [phases, setPhases] = useState<ProjectPhase[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const provider = React.useMemo(() => new ProjectPhasesRestDataProvider(), []);
  const columns: DetailedListColumn<ProjectPhase>[] = React.useMemo(() => [
    { key: 'name', header: t('screens:projectPhases.columns.phaseName'), dataField: 'name', type: 'string' },
    { key: 'order', header: t('screens:projectPhases.columns.order'), dataField: 'order', type: 'number', align: 'center' },
  ], [t]);

  useEffect(() => {
    setLoading(true);
    provider.getAll()
      .then(data => setPhases(data.map((p, i) => ({ ...p, order: i + 1 }))))
      .catch((e: any) => setError(e.message))
      .finally(() => setLoading(false));
  }, [provider]);

  const handleChange = async (newPhases: ProjectPhase[]) => {
    setLoading(true);
    setError(null);
    try {
      // Find added, updated, and deleted phases
      const added = newPhases.filter(np => !phases.some(p => p.id === np.id));
      const removed = phases.filter(p => !newPhases.some(np => np.id === p.id));
      const updated = newPhases.filter(np => phases.some(p => p.id === np.id && (p.name !== np.name)));

      for (const phase of added) await provider.create(phase);
      for (const phase of updated) await provider.update(phase.id, phase);
      for (const phase of removed) await provider.delete(phase.id);

      // Reload from server
      const fresh = await provider.getAll();
      setPhases(fresh.map((p, i) => ({ ...p, order: i + 1 })));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="project-phases-tab">
      <SubsectionPanel title={t('screens:projectPhases.title')}>
        {error && <div style={{ color: 'red', marginBottom: 12 }}>{error}</div>}
        <InfoPanel header="About these phases">
          The phases on this screen will be provisioned by default when a new project is created. You can add, edit, or remove phases as needed. The order of the phases is important and will determine the sequence in which they are presented in the project workflow.
        </InfoPanel>
        <DetailedListComponent
          dataSource={{ data: phases, setData: setPhases }}
          columns={columns}
          rowKey={p => p.id}
          onChange={handleChange}
        />
        {loading && <div style={{ marginTop: 12 }}>{t('common:loading')}</div>}
      </SubsectionPanel>
    </div>
  );
};

export default ProjectPhasesTab;
